import { Box, ButtonBase, Typography, Skeleton, Chip, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { colors, typography as t, radius } from '@/theme/tokens';

export interface StatCardProps {
  label: string;
  value: number;
  /** Overrides the formatted numeric value as the headline (e.g. "91%"). */
  valueText?: string;
  valueColor?: string;
  /** Small chip rendered after the value (e.g. a percentage). */
  percent?: number;
  /** Non-color status channel rendered before the value (a11y fallback). */
  statusGlyph?: ReactNode;
  isLoading: boolean;
  /** When provided the card is an actionable button; otherwise a static panel. */
  onClick?: () => void;
  /** Optional help text — clarifies what the number means (e.g. its window). */
  tooltip?: string;
}

/**
 * Reusable stat card. Extracted from the dashboard so the Fleet Analytics KPI
 * band reuses the exact same chrome. Uses theme palette for dark-mode support.
 */
export function StatCard({
  label,
  value,
  valueText,
  valueColor,
  percent,
  statusGlyph,
  isLoading,
  onClick,
  tooltip,
}: StatCardProps) {
  const baseSx = {
    flex: 1,
    minWidth: 160,
    display: 'block',
    textAlign: 'left' as const,
    bgcolor: 'background.paper',
    borderRadius: `${radius.lg}px`,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 1,
    p: '20px',
  };

  const content = (
    <>
      <Typography sx={{ color: 'text.secondary', fontSize: t.fontSize.sm, fontWeight: t.fontWeight.medium, mb: 1 }}>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={60} height={36} />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          {statusGlyph !== undefined && (
            <Box component="span" aria-hidden sx={{ color: valueColor, fontSize: t.fontSize.lg, lineHeight: 1 }}>
              {statusGlyph}
            </Box>
          )}
          <Typography sx={{
            color: valueColor || 'text.primary',
            fontSize: t.fontSize['2xl'],
            fontWeight: t.fontWeight.bold,
            fontFamily: t.fontFamily,
            lineHeight: 1,
          }}>
            {valueText ?? value.toLocaleString()}
          </Typography>
          {percent !== undefined && (
            <Chip
              label={`${percent}%`}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: t.fontWeight.medium,
                bgcolor: valueColor ? alpha(valueColor, 0.094) : 'action.hover',
                color: valueColor || 'text.secondary',
              }}
            />
          )}
        </Box>
      )}
    </>
  );

  const card = onClick ? (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        ...baseSx,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 3 },
        '&:focus-visible': { outline: `2px solid ${colors.primary.main}`, outlineOffset: 2 },
      }}
    >
      {content}
    </ButtonBase>
  ) : (
    <Box sx={baseSx}>{content}</Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}

export default StatCard;
